import React, { useState } from 'react';
import Honeypot from './Honeypot';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Linkedin, Facebook, Mail } from 'lucide-react';
import { XIcon } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Footer = () => {
  const [email, setEmail] = useState('');
  const [hp, setHp] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/newsletter/subscribe`, { email, orb_hp: hp });
      
      if (response.data) {
        toast.success('Thank you for subscribing! We\'ll keep you updated.');
        setEmail('');
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      if (error.response?.status === 201) {
        // Success but returned existing subscription
        toast.success('You\'re already subscribed!');
        setEmail('');
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-black border-t border-gray-800">
      <div className="container mx-auto px-6 py-12 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Brand */}
          <div>
            <div className="mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_lifecycle-preview/artifacts/fsdi0cmg_Orboroi_green_white_letters.png" 
                alt="Orboroi" 
                className="h-6"
              />
            </div>
            <p className="text-gray-400 text-sm mb-4">
              Precision in Every Pixel. Certainty in Your AI Models.
            </p>
            <div className="flex space-x-4">
              <a 
                href="https://www.linkedin.com/company/orboroi" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-[#379F7F] transition-colors duration-300"
              >
                <Linkedin size={20} />
              </a>
              <a 
                href="https://www.facebook.com/orboroi" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-[#379F7F] transition-colors duration-300"
              >
                <Facebook size={20} />
              </a>
              <a 
                href="https://x.com/orboroi" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-[#379F7F] transition-colors duration-300"
              >
                <XIcon size={20} />
              </a>
              <a 
                href="mailto:contact@orboroi.com"
                className="text-gray-400 hover:text-[#379F7F] transition-colors duration-300"
              >
                <Mail size={20} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-gray-400 hover:text-[#379F7F] text-sm transition-colors duration-300">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/solutions" className="text-gray-400 hover:text-[#379F7F] text-sm transition-colors duration-300">
                  Solutions
                </Link>
              </li>
              <li>
                <Link to="/process" className="text-gray-400 hover:text-[#379F7F] text-sm transition-colors duration-300">
                  Our Process
                </Link>
              </li>
              <li>
                <Link to="/case-studies" className="text-gray-400 hover:text-[#379F7F] text-sm transition-colors duration-300">
                  Case Studies
                </Link>
              </li>
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="text-white font-semibold mb-4">Resources</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/resources" className="text-gray-400 hover:text-[#379F7F] text-sm transition-colors duration-300">
                  Blog & Articles
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-gray-400 hover:text-[#379F7F] text-sm transition-colors duration-300">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-400 hover:text-[#379F7F] text-sm transition-colors duration-300">
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="text-white font-semibold mb-4">Stay Updated</h4>
            <p className="text-gray-400 text-sm mb-4">
              Get AI Model Lifecycle Best Practices in your inbox
            </p>
            <form onSubmit={handleNewsletterSubmit} className="space-y-2">
              <Honeypot value={hp} onChange={setHp} />
              <Input
                type="email"
                placeholder="Your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-gray-900 border-gray-700 text-white"
                required
              />
              <Button 
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#379F7F] text-white hover:bg-[#379F7F]/90 font-semibold disabled:opacity-50"
              >
                {isSubmitting ? 'Subscribing...' : 'Subscribe'}
              </Button>
            </form>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center">
          <p className="text-gray-400 text-sm">
            © {new Date().getFullYear()} Orboroi. All rights reserved.
          </p>
          <div className="flex space-x-6 mt-4 md:mt-0">
            <Link to="/privacy-policy" className="text-gray-400 hover:text-[#379F7F] text-sm transition-colors duration-300">
              Privacy Policy
            </Link>
            <Link to="/terms-of-service" className="text-gray-400 hover:text-[#379F7F] text-sm transition-colors duration-300">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
